import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { DecimalPipe, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  EMAIL_CREDITS_PER_FOUND,
  LeadExport,
  MAX_EXPORT_LEADS,
  ProspectingListConfig,
  ProspectingService,
  apiErrorMessage,
  exportLeadsCost,
} from '../../../../services/prospecting.service';
import { PopupService } from '../../../../services/popup.service';
import { ExportColumnsPickerComponent } from '../export-columns-picker/export-columns-picker.component';
import { LeadExportColumn, loadExportColumns, saveExportColumns } from '../../../../utils/lead-export-columns';

/**
 * "More than 10 → Export to Excel": generates 11–2000 (or all) leads of a
 * prospecting list in the background, delivered as an .xlsx on the Exports page.
 */
@Component({
  selector: 'app-list-export-dialog',
  standalone: true,
  imports: [NgIf, FormsModule, DecimalPipe, ExportColumnsPickerComponent],
  templateUrl: './list-export-dialog.component.html',
})
export class ListExportDialogComponent implements OnInit, OnDestroy {
  @Input({ required: true }) listId!: string;
  @Input({ required: true }) config!: ProspectingListConfig;
  @Input() companiesLeft: number | null = null;
  @Input() balance: number | null = null;
  @Input() initialMode: 'custom' | 'all' = 'custom';
  @Input() initialCount = 50;
  @Output() started = new EventEmitter<LeadExport>();
  @Output() closed = new EventEmitter<void>();

  readonly maxLeads = MAX_EXPORT_LEADS;

  mode: 'custom' | 'all' = 'custom';
  count = 50;
  includeEmails = false;
  columns: LeadExportColumn[] = loadExportColumns();
  isStarting = false;
  errorMessage = '';

  private destroy$ = new Subject<void>();

  constructor(
    private prospectingService: ProspectingService,
    private popupService: PopupService,
  ) {}

  ngOnInit(): void {
    this.mode = this.initialMode;
    this.count = this.clampCount(this.initialCount);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private clampCount(value: number): number {
    const n = Math.floor(Number(value) || 0);
    return Math.min(MAX_EXPORT_LEADS, Math.max(11, n));
  }

  get countValid(): boolean {
    const n = Number(this.count);
    return Number.isInteger(n) && n >= 11 && n <= MAX_EXPORT_LEADS;
  }

  /** Lead count the cost lines are based on. */
  get effectiveCount(): number {
    if (this.mode === 'all') {
      const left = this.companiesLeft ?? this.config?.total_companies ?? MAX_EXPORT_LEADS;
      return Math.max(11, Math.min(MAX_EXPORT_LEADS, left || MAX_EXPORT_LEADS));
    }
    return this.countValid ? Number(this.count) : 0;
  }

  get leadsCost(): number {
    return this.effectiveCount ? exportLeadsCost(this.effectiveCount) : 0;
  }

  onColumnsChange(columns: LeadExportColumn[]): void {
    this.columns = columns;
    saveExportColumns(columns);
  }

  get emailColumn(): boolean {
    return this.columns.includes('email');
  }

  /** Emails are only revealed (and charged) when the Email column is exported. */
  get withEmails(): boolean {
    return this.includeEmails && this.emailColumn;
  }

  get emailsCost(): number {
    return this.withEmails ? EMAIL_CREDITS_PER_FOUND * this.effectiveCount : 0;
  }

  get totalCost(): number {
    return this.leadsCost + this.emailsCost;
  }

  get shortOfCredits(): boolean {
    return this.balance !== null && this.balance < this.totalCost;
  }

  close(): void {
    if (this.isStarting) {
      return;
    }
    this.closed.emit();
  }

  start(): void {
    if (this.isStarting) {
      return;
    }
    if (this.mode === 'custom' && !this.countValid) {
      this.errorMessage = `Enter a number of leads between 11 and ${MAX_EXPORT_LEADS}.`;
      return;
    }

    this.errorMessage = '';
    this.isStarting = true;

    this.prospectingService.startExport(
      this.listId, this.config, this.mode, this.mode === 'custom' ? Number(this.count) : null, this.withEmails, this.columns,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ export: exp }) => {
          this.isStarting = false;
          saveExportColumns(this.columns);
          this.popupService.notify('Export started — you can leave this page, the file lands on the Exports page.', '', 'success');
          this.started.emit(exp);
        },
        error: (error) => {
          this.isStarting = false;
          const status = error?.status;
          if (status === 409 && error?.error?.export) {
            this.popupService.notify('An export of this list is already running.', '', 'info');
            this.started.emit(error.error.export as LeadExport);
            return;
          }
          if (status === 402) {
            const required = error?.error?.required;
            this.errorMessage = required
              ? `Not enough credits — starting this export needs ${required} credits.`
              : 'Not enough credits to start this export.';
            return;
          }
          if (status === 422) {
            const errors = error?.error?.errors;
            const first = errors ? Object.values(errors).flat()[0] : null;
            this.errorMessage = (typeof first === 'string' && first) || apiErrorMessage(error, 'Check the export settings and try again.');
            return;
          }
          this.errorMessage = apiErrorMessage(error, 'Could not start the export. Please try again.');
        }
      });
  }
}
